import { useEffect, useId, useState } from 'react';
import { Invisible } from '../ui';
import { messageDErreur, useSession } from './session';
import { useTraduction } from '../i18n/langue';
import { messageFournisseur } from './echecFournisseur';
import { sessionAuthentification } from '../natif/connexionTierce';
import { estNatif } from '../natif/pont';

/**
 * Connexion par un fournisseur tiers (T-041).
 *
 * Chaque bouton n'est actif que si le **serveur** déclare savoir le servir : la
 * disponibilité se demande à `/auth/providers`, elle ne se devine pas depuis le client.
 * Un bouton qui échouerait au clic serait pire que son absence, et une configuration
 * incomplète en production doit se voir ici, pas dans un message d'erreur après trois pas.
 *
 * Les boutons inactifs restent **affichés** plutôt que cachés : le parent voit ce qui
 * arrive. L'indisponibilité se lit alors de trois façons, parce qu'aucune ne suffit
 * seule : l'infobulle au survol, l'aspect désaturé, et un texte lié en `aria-describedby`
 * — sans lui, un lecteur d'écran annoncerait « bouton indisponible » sans dire pourquoi.
 * L'infobulle est portée par l'enveloppe et non par le bouton : un bouton désactivé ne
 * reçoit pas les événements de souris, et son `title` ne s'afficherait pas.
 */

function MarqueGoogle() {
  return (
    <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

function MarqueApple() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M17.05 12.54c-.03-2.7 2.2-4 2.3-4.06-1.25-1.83-3.2-2.08-3.89-2.11-1.66-.17-3.24.97-4.08.97-.84 0-2.14-.95-3.52-.92-1.81.03-3.48 1.05-4.41 2.67-1.88 3.26-.48 8.09 1.35 10.74.9 1.3 1.97 2.75 3.38 2.7 1.36-.06 1.87-.88 3.51-.88 1.64 0 2.1.88 3.53.85 1.46-.02 2.38-1.32 3.27-2.63 1.03-1.51 1.46-2.97 1.48-3.05-.03-.01-2.84-1.09-2.87-4.32M14.6 4.6c.74-.9 1.24-2.15 1.1-3.4-1.07.04-2.36.71-3.13 1.61-.69.8-1.29 2.07-1.13 3.29 1.19.09 2.42-.6 3.16-1.5"
      />
    </svg>
  );
}

/**
 * La marque de Meta — l'anneau en forme d'infini, tracé au trait.
 *
 * Dessiné plutôt qu'importé : la charte de Meta demande le fichier officiel, mais aucune
 * dépendance ne mérite d'entrer dans le paquet du navigateur pour dix-huit pixels. À
 * remplacer par le SVG de la marque le jour où l'application Meta sera déclarée.
 */
function MarqueMeta() {
  return (
    <svg viewBox="0 0 32 20" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="#0081FB"
        strokeWidth="3.2"
        strokeLinecap="round"
        d="M2.6 12.6C2.6 6.9 5.4 2.6 9 2.6c2.6 0 4.4 1.9 6.9 6 2.4-4.1 4.3-6 6.9-6 3.6 0 6.4 4.3 6.4 10 0 3.1-1.6 4.8-3.7 4.8-2.6 0-4.2-2.4-6.4-6.3-1.1-1.9-2.1-3.6-3.2-3.6-1.1 0-2.1 1.7-3.2 3.6-2.2 3.9-3.8 6.3-6.4 6.3-2.1 0-3.7-1.7-3.7-4.8Z"
      />
    </svg>
  );
}

interface Disponibilite {
  google: boolean;
  apple: boolean;
  meta: boolean;
}

export function FournisseursTiers({
  action,
  surDepart = (url) => globalThis.location.assign(url),
  surSession,
}: {
  action: 'connexion' | 'inscription';
  /**
   * Le départ vers le fournisseur. Injectable pour les tests — même parti que la fabrique
   * de WebSocket du client d'API : une navigation réelle n'est pas observable en test, et
   * la simuler en remplaçant `location` casse plus qu'elle n'éprouve.
   */
  surDepart?: (url: string) => void;
  /**
   * La session vient de s'ouvrir **ici**, sans que la page ait bougé.
   *
   * N'arrive que dans la coque native : le navigateur, lui, part chez le fournisseur et
   * revient sur `/connexion?reprise=…`, où l'écran de connexion termine et navigue. Dans
   * l'application il n'y a ni départ ni retour — la session s'ouvre sous le formulaire, et
   * sans ce signal l'écran de connexion reste affiché devant un parent pourtant connecté.
   * Constaté sur téléphone : quitter puis rouvrir l'application montrait la session
   * ouverte, parce que les gardes de route ne se rejouent qu'à une navigation.
   */
  surSession?: () => void;
}) {
  const { client, reprendreConnexion } = useSession();
  const { messages } = useTraduction();
  const id = useId();
  const noteId = `${id}-note`;
  const verbe = action === 'connexion' ? messages.entree.connexion.verbe : messages.entree.inscription.verbe;

  // `null` tant qu'on ne sait pas : on n'affiche pas un bouton actif par optimisme, ni un
  // bouton barré par pessimisme, avant que le serveur ait répondu.
  const [disponible, setDisponible] = useState<Disponibilite | null>(null);
  const [partant, setPartant] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    let annule = false;
    void client
      .request<Disponibilite>('/auth/providers')
      .then((d) => {
        if (!annule) setDisponible(d);
      })
      .catch(() => {
        // L'indisponibilité du serveur n'est pas celle des fournisseurs, mais le résultat
        // pour le parent est le même : ces boutons ne mènent nulle part maintenant.
        if (!annule) setDisponible({ google: false, apple: false, meta: false });
      });
    return () => {
      annule = true;
    };
  }, [client]);

  /**
   * Quitte l'application pour le fournisseur.
   *
   * C'est le serveur qui fabrique l'adresse : l'état, le nonce et le vérificateur PKCE
   * doivent naître et être vérifiés côté serveur, sans quoi ils ne prouvent rien.
   *
   * Deux sorties, et c'est la seule chose que la coque native change ici :
   *
   * - **Dans un navigateur**, on s'en va. Le fournisseur renvoie sur `/connexion?reprise=…`,
   *   et c'est l'écran de connexion qui termine — le code de reprise est dans l'adresse
   *   parce que le navigateur est parti et revenu.
   * - **Dans l'application**, il n'y a nulle part où aller : la WebView *est* l'application.
   *   Le système ouvre un navigateur par-dessus, le referme au retour, et rend l'adresse.
   *   La session s'ouvre alors ici même, sans passer par l'URL.
   *
   * L'origine annoncée au serveur (`web` ou `mobile`) commande l'adresse de retour qu'il
   * déclare au fournisseur : la même valeur des deux côtés, sinon le consentement aboutit
   * sur une page morte.
   */
  async function partir(cle: string): Promise<void> {
    setPartant(cle);
    setErreur(null);
    try {
      const { authorizationUrl } = await client.request<{ authorizationUrl: string }>(
        `/auth/${cle}/start`,
        { method: 'POST', body: { origin: estNatif() ? 'mobile' : 'web' } },
      );

      if (!estNatif()) {
        surDepart(authorizationUrl);
        return;
      }

      const retour = await sessionAuthentification(authorizationUrl);
      // Fermeture volontaire : le parent a changé d'avis, il n'a rien raté.
      if (retour === null) {
        setPartant(null);
        return;
      }

      const motif = retour.get('erreur');
      if (motif !== null) {
        setErreur(messageFournisseur(motif, messages.entree.connexion));
        setPartant(null);
        return;
      }

      const reprise = retour.get('reprise');
      if (reprise === null) {
        setErreur(messageFournisseur('impossible', messages.entree.connexion));
        setPartant(null);
        return;
      }

      await reprendreConnexion(reprise);
      // La session est ouverte : c'est à l'écran qui nous porte de quitter les lieux. Le
      // bouton reste sur « Redirection… » jusque-là, et c'est voulu — la seule suite
      // possible est de partir.
      surSession?.();
    } catch (cause) {
      setErreur(messageDErreur(cause));
      setPartant(null);
    }
  }

  const fournisseurs = [
    { cle: 'google', nom: 'Google', marque: <MarqueGoogle />, actif: disponible?.google === true },
    { cle: 'apple', nom: 'Apple', marque: <MarqueApple />, actif: disponible?.apple === true },
    // Meta en dernier : l'ordre suit ce que les parents emploient le plus pour entrer, et
    // la place la plus haute revient à celui qui aboutit le plus souvent.
    { cle: 'meta', nom: 'Meta', marque: <MarqueMeta />, actif: disponible?.meta === true },
  ];

  return (
    <section className="fournisseurs" aria-labelledby={`${id}-titre`}>
      <p className="separateur">
        <span>{messages.entree.fournisseurs.separateurOu}</span>
      </p>

      <p className="fournisseurs-intro" id={`${id}-titre`}>
        {messages.entree.fournisseurs.intro}
      </p>

      <div className="fournisseurs-boutons">
        {fournisseurs.map((fournisseur) => (
          // L'infobulle vit sur l'enveloppe : un bouton désactivé ne reçoit pas le survol.
          <span
            key={fournisseur.cle}
            className="fournisseur-enveloppe"
            {...(fournisseur.actif ? {} : { title: messages.entree.fournisseurs.indisponible })}
          >
            <button
              type="button"
              className={`bouton fournisseur ${fournisseur.cle}`}
              disabled={!fournisseur.actif || partant !== null}
              onClick={() => void partir(fournisseur.cle)}
              {...(fournisseur.actif ? {} : { 'aria-describedby': noteId })}
            >
              <span className="fournisseur-marque">{fournisseur.marque}</span>
              <span>
                {partant === fournisseur.cle
                  ? messages.entree.fournisseurs.redirection(fournisseur.nom)
                  : messages.entree.fournisseurs.verbeAvec(verbe, fournisseur.nom)}
              </span>
            </button>
          </span>
        ))}
      </div>

      {erreur !== null ? (
        <p className="fournisseurs-erreur" role="alert">
          {erreur}
        </p>
      ) : null}

      <Invisible>
        <span id={noteId}>{messages.entree.fournisseurs.indisponibleDetail}</span>
      </Invisible>
    </section>
  );
}
